"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createFundingReportingMechanics = exports.createFundingMechanics = void 0;
const lodash_1 = __importDefault(require("lodash"));
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const routing_1 = require("./routing");
// Funding mechanics creation is responsible for generating the template based on CA and associated report values
// Current assumptions:
// 1) USD only
// 2) Lender can only belong to one class
const createFundingMechanics = ({ creditAgreement, borrowingBaseReport, cashBalances, pricingOptions, fundingRequestId, fundingRequestComponents, customOverrides, logOptions, }) => {
    const log = (params) => {
        if (logOptions?.logger) {
            logOptions.logger(params, logOptions.logLevel);
        }
    };
    // Components are required for server<>client coordination at this time. This may be removed if funding request records are not created prior to page load.
    log({
        message: `createFundingMechanics: initial creation of funding mechanics [fundingRequestId:${fundingRequestId}] [borrowingBaseReportId:${borrowingBaseReport.id}]`,
    });
    const recycleComponents = fundingRequestComponents.filter((component) => component.type ===
        finley_types_1.FundingRequest.FundingRequestComponentType.CollectionsRecycle);
    (0, common_1.finleyAssert)(recycleComponents.length > 0, new common_1.error.FinleyError(`Create Funding Mechanics Error: missing collections recycle components for [fundingRequestId:${fundingRequestId}]`, {
        metadata: {
            logMeta: {
                fundingRequestComponents: JSON.stringify(fundingRequestComponents),
            },
        },
    }));
    // ***** COMMITMENTS *****
    const { classMechanics, facilityMechanics: { parties: { borrowers, capitalProviders: lenders }, }, fundingRequestMechanics, } = creditAgreement;
    log({
        message: `createFundingMechanics: managing following lenders [fundingRequestId:${fundingRequestId}] [lenders:${lenders
            .map((l) => l.entity.name)
            .join(', ')}]`,
        metadata: { logMeta: { lenders: JSON.stringify(lenders) } },
    });
    // ***** REQUEST ROUTING *****
    // This method already handles the correct counts of source/destination for current expectations
    const requestRoutings = (0, routing_1.getRequestRoutings)({
        parties: [...borrowers, ...lenders],
        fundingRequestMechanics,
    });
    log({
        message: `createFundingMechanics: managing ${requestRoutings.length} request routings for [fundingRequestId:${fundingRequestId}]`,
        metadata: { logMeta: { requestRoutings: JSON.stringify(requestRoutings) } },
    });
    // ***** EMPTY FUNDING MECHANICS *****
    const fundingMechanics = {
        fundingRequestId,
        commitments: [],
        draw: {
            allocation: {
                strategy: 'proportion',
                strict: !customOverrides?.independentClassAllocation,
            },
            constraints: [],
            pricingOptions: pricingOptions.map((option) => ({
                key: option.key,
                name: option.name,
                lookbackDays: option.lookbackDays,
                invoiceMechanics: option.invoiceMechanics,
            })),
            targetFundingDate: common_1.date.currentDateStr(), // Override when processing CA
        },
        recycles: [],
        requestRoutings,
    };
    const drawComponents = fundingRequestComponents.filter((component) => component.type === finley_types_1.FundingRequest.FundingRequestComponentType.DrawRequest);
    (0, common_1.finleyAssert)(drawComponents.length > 0, new common_1.error.FinleyError(`Create Funding Mechanics Error: missing draw request components for [fundingRequestId:${fundingRequestId}]`, {
        metadata: {
            logMeta: {
                fundingRequestComponents: JSON.stringify(fundingRequestComponents),
            },
        },
    }));
    log({
        message: `createFundingMechanics: managing ${drawComponents.length} draw components for [fundingRequestId:${fundingRequestId}]`,
        metadata: { logMeta: { drawComponents: JSON.stringify(drawComponents) } },
    });
    // Commitments in CA are equivalent to shares
    const lenderShares = lenders.map((lender) => {
        // Find draw request routing
        const drawRequestRouting = requestRoutings.find((routing) => routing.type ===
            finley_types_1.FundingRequest.FundingRequestComponentType.DrawRequest &&
            routing.source.organizationId === lender.entity.id);
        (0, common_1.finleyAssert)(drawRequestRouting, new common_1.error.FinleyError(`Create Funding Mechanics Error: unable to find draw request bank account routing associated with lender (${lender.entity.name}) in credit agreement configuration [fundingRequestId:${fundingRequestId}]`, {
            metadata: {
                logMeta: {
                    lenders: JSON.stringify(lenders),
                    requestRoutings: JSON.stringify(requestRoutings),
                },
            },
        }));
        const drawComponent = drawComponents.find((component) => component.sourceBankAccountId === drawRequestRouting.source.accountId);
        (0, common_1.finleyAssert)(drawComponent, new common_1.error.FinleyError(`Create Funding Mechanics Error: unable to find draw request component associated with lender (${lender.entity.name}) for [fundingRequestId:${fundingRequestId}]`, {
            metadata: {
                logMeta: {
                    drawComponents: JSON.stringify(drawComponents),
                    accountId: drawRequestRouting.source.accountId,
                },
            },
        }));
        const share = {
            lenderId: lender.entity.id,
            lenderName: lender.entity.name,
            amount: lender.commitmentAmount.value,
            fundingRequestComponentId: drawComponent.id,
            routing: {
                source: {
                    bankAccountId: drawRequestRouting.source.accountId,
                    label: drawRequestRouting.source.accountName,
                },
                destination: {
                    bankAccountId: drawRequestRouting.destination.accountId,
                    label: drawRequestRouting.destination.accountName,
                },
            },
            applicableMargin: lender.applicableMarginBeginning?.value ?? 0, // TODO: inherit global setting
        };
        return share;
    });
    if (classMechanics) {
        const { classDetails } = borrowingBaseReport;
        (0, common_1.finleyAssert)(classDetails.classAttachmentLimits, `Create Funding Mechanics Error: missing class attachment limits from borrowing base class details for [fundingRequestId:${fundingRequestId}] [borrowingBaseReportId:${borrowingBaseReport.id}]`);
        for (const fundingClass of classMechanics.classes) {
            const processedClassAttachmentLimit = classDetails.classAttachmentLimits.find(({ classLabel }) => classLabel === fundingClass.classLabel.value);
            (0, common_1.finleyAssert)(processedClassAttachmentLimit, `Create Funding Mechanics Error: unable to find processed attachment limit values for class ${fundingClass.classLabel.value.toUpperCase()} [fundingRequestId:${fundingRequestId}]`);
            const classLenderIds = fundingClass.capitalProviders.map(({ entity }) => entity.id);
            const classShares = lenderShares.filter((share) => classLenderIds.includes(share.lenderId));
            const classCommitment = lodash_1.default.sumBy(classShares, (lc) => lc.amount);
            fundingMechanics.commitments.push({
                key: fundingClass.classLabel.value,
                label: customOverrides?.classLabels?.[fundingClass.classLabel.value] ??
                    `Class ${fundingClass.classLabel.value.toUpperCase()}`,
                amount: classCommitment,
                attachmentRate: processedClassAttachmentLimit.attachmentRateLimit,
                shares: classShares,
            });
        }
    }
    else {
        const totalCommitment = lodash_1.default.sumBy(lenderShares, (lc) => lc.amount);
        fundingMechanics.commitments.push({
            key: 'fixed',
            label: '',
            amount: totalCommitment,
            attachmentRate: 1,
            shares: lenderShares,
        });
    }
    const { properties, collectionsRecycle, drawRequest, } = fundingRequestMechanics;
    // ***** DRAW MECHANICS *****
    (0, common_1.finleyAssert)(drawRequest.submissionNoticePeriod, `Create Funding Mechanics Error: missing submission notice period for draw request from credit agreement configuration [fundingRequestId:${fundingRequestId}]`);
    (0, common_1.finleyAssert)(drawRequest.submissionNoticePeriod.unit === 'day' ||
        drawRequest.submissionNoticePeriod.unit === 'businessDay', `Create Funding Mechanics Error: invalid submission notice period units for draw request in credit agreement configuration [fundingRequestId:${fundingRequestId}]`);
    fundingMechanics.draw.targetFundingDate =
        drawRequest.submissionNoticePeriod.unit === 'businessDay'
            ? common_1.date.addBusinessDays(common_1.date.currentDateStr(), drawRequest.submissionNoticePeriod.value)
            : common_1.date.addDays(common_1.date.currentDateStr(), drawRequest.submissionNoticePeriod.value);
    // Individual mechanic handling for the current CA structure
    if (drawRequest.classAllocationMethod &&
        drawRequest.classAllocationMethod.value !==
            finley_types_1.FundingRequest.ClassDrawRequestAllocationMethod
                .FixedAttachmentRateLimitProportion) {
        fundingMechanics.draw.allocation.strategy = 'cascade';
    }
    if (drawRequest.minimumAmount) {
        fundingMechanics.draw.constraints.push({
            type: 'minimum',
            label: 'Minimum draw request',
            value: drawRequest.minimumAmount.value,
        });
    }
    if (drawRequest.incrementAmount) {
        // Unhandled for now
    }
    if (drawRequest.maximumAmountPerPeriod && drawRequest.maximumAmountPeriod) {
        // Unhandled for now
    }
    if (drawRequest.maximumFrequency) {
        // Unhandled for now
    }
    if (drawRequest.isStopDrawEnforced && drawRequest.isStopDrawEnforced.value) {
        fundingMechanics.draw.constraints.push({
            type: 'condition',
            label: 'Draw required sufficient collections availability',
            condition: 'stopDraw',
            value: 1,
        });
    }
    if (properties && properties.maximumPrefundingAmount) {
        // TODO: temporary hack - move to full evaluation using constraint as input
        fundingMechanics.draw.constraints.push({
            type: 'condition',
            label: 'Prefunded draw enabled',
            condition: 'prefundingEnabled',
            value: 1,
        });
    }
    // ***** RECYCLE MECHANICS *****
    const collectionsAccounts = cashBalances.filter(({ accountType }) => accountType === finley_types_1.Bank.BorrowerAccountType.Collections);
    if (collectionsAccounts.length === 0 ||
        !collectionsRecycle.isAllowed?.value) {
        return fundingMechanics;
    }
    // Individual mechanic handling for the current CA structure
    const collectionsRecycleRoutings = requestRoutings.filter((routing) => routing.type ===
        finley_types_1.FundingRequest.FundingRequestComponentType.CollectionsRecycle);
    (0, common_1.finleyAssert)(collectionsRecycleRoutings.length === collectionsAccounts.length, `Create Funding Mechanics Error: unable to determine collections recycle bank routing from credit agreement, expected [${collectionsAccounts.length}] available option but received ${collectionsRecycleRoutings.length} [fundingRequestId:${fundingRequestId}]`);
    (0, common_1.finleyAssert)(collectionsRecycle.submissionNoticePeriod, `Create Funding Mechanics Error: unable to determine submission notice period for collections recycle from credit agreement configuration [fundingRequestId:${fundingRequestId}]`);
    (0, common_1.finleyAssert)(collectionsRecycle.submissionNoticePeriod.unit === 'day' ||
        collectionsRecycle.submissionNoticePeriod.unit === 'businessDay', `Create Funding Mechanics Error: invalid submission notice period units for collections recycle in credit agreement configuration [fundingRequestId:${fundingRequestId}]`);
    if (collectionsRecycle.minimumAmount) {
        // Unhandled for now
    }
    if (collectionsRecycle.incrementAmount) {
        // Unhandled for now
    }
    if (collectionsRecycle.maximumFrequency) {
        // Unhandled for now
    }
    const recycleTargetFundingDate = collectionsRecycle.submissionNoticePeriod.unit === 'businessDay'
        ? common_1.date.addBusinessDays(common_1.date.currentDateStr(), collectionsRecycle.submissionNoticePeriod.value)
        : common_1.date.addDays(common_1.date.currentDateStr(), collectionsRecycle.submissionNoticePeriod.value);
    recycleComponents.forEach((recycleComponent) => {
        // find collectionsRecycleRouting associated with recycleComponent
        const collectionsRecycleRouting = collectionsRecycleRoutings.find((routing) => {
            return (routing.source.accountId === recycleComponent.sourceBankAccountId);
        });
        (0, common_1.finleyAssert)(collectionsRecycleRouting, `Collections recycle routing not found for recycle component with [sourceBankAccountId: ${recycleComponent.sourceBankAccountId}]`);
        fundingMechanics.recycles.push({
            constraints: [],
            targetFundingDate: recycleTargetFundingDate,
            fundingRequestComponentId: recycleComponent.id,
            routing: {
                source: {
                    bankAccountId: collectionsRecycleRouting.source.accountId,
                    label: collectionsRecycleRouting.source.accountName,
                },
                destination: {
                    bankAccountId: collectionsRecycleRouting.destination.accountId,
                    label: collectionsRecycleRouting.destination.accountName,
                },
            },
        });
    });
    if (collectionsRecycle.limitToNewlyAssignedEligibleExposureSinceLastApprovedFundingRequest &&
        collectionsRecycle
            .limitToNewlyAssignedEligibleExposureSinceLastApprovedFundingRequest.value) {
        fundingMechanics.recycles.forEach((recycle) => {
            recycle.constraints.push({
                type: 'condition',
                label: 'Limit recycle to newly assigned eligible exposure since last approved funding request',
                condition: 'limitToNewlyAssignedEligibleExposureSinceLastApprovedFundingRequest',
                value: 1,
            });
        });
    }
    return fundingMechanics;
};
exports.createFundingMechanics = createFundingMechanics;
// Reporting mechanics is generated to send through important CA information regarding
// report generation with regards to funding requests
const createFundingReportingMechanics = ({ creditAgreement, }) => {
    const { reportingMechanics } = creditAgreement;
    const fundingNotices = reportingMechanics.filter((report) => report.reportConfigTypes.some((configType) => configType === 'fundingNotice'));
    return {
        fundingNotices: fundingNotices.map((fundingNoticeConfig) => {
            const { _config_: config } = fundingNoticeConfig;
            const fundingRequestComponents = [];
            (0, common_1.finleyAssert)(config.type === 'docx', 'Only docx configuration type is supported to get funding notice.');
            if (config.output.fundingRequestComponents) {
                lodash_1.default.toPairs(config.output.fundingRequestComponents).reduce((acc, [fundingRequestComponentType, generateForComponent]) => {
                    if (generateForComponent) {
                        (0, common_1.finleyAssert)(common_1.typeguards.isFundingRequestComponentType(fundingRequestComponentType), `Invalid funding request component type: ${fundingRequestComponentType}`);
                        acc.push(fundingRequestComponentType);
                    }
                    return acc;
                }, fundingRequestComponents);
            }
            return {
                reportName: config.output.name,
                fundingRequestComponents,
            };
        }),
    };
};
exports.createFundingReportingMechanics = createFundingReportingMechanics;
//# sourceMappingURL=mechanics.js.map